import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import BlogArticleLayout from '@/components/base/BlogArticleLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Blog', to: '/blog/' },
  { label: 'Baie coulissante qui coince' },
];

const jsonLdBlogPosting = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: 'Baie coulissante qui coince ou dure à ouvrir : que faire',
  description: 'Votre baie coulissante coince ou devient dure à ouvrir ? Causes possibles (roulettes, rail, désalignement), vérifications à faire soi-même et quand appeler un professionnel dans l\'Hérault.',
  url: 'https://heraultroulettesbaiesvitrees.fr/blog/baie-coulissante-qui-coince-que-faire/',
  datePublished: '2026-06-20',
  dateModified: '2026-07-26',
  author: { '@type': 'Organization', name: 'DFPRO Hérault' },
  publisher: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Pourquoi ma baie coulissante est-elle devenue dure à ouvrir ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Trois causes principales : des roulettes usées ou grippées qui ne tournent plus librement, un rail encrassé ou déformé qui bloque la course du vantail, ou un désalignement du vantail qui frotte contre le cadre. Un diagnostic sur place permet d\'identifier précisément l\'origine de la résistance.',
      },
    },
    {
      '@type': 'Question',
      name: 'Que puis-je vérifier moi-même avant d\'appeler un réparateur ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Vous pouvez inspecter visuellement le rail : y a-t-il des débris, du sable, des cailloux coincés ? Vérifiez aussi que rien ne bloque la partie haute du vantail. En revanche, ne forcez jamais sur la baie et ne tentez pas de démonter le vantail seul, car il pèse plus de 80 kg.',
      },
    },
    {
      '@type': 'Question',
      name: 'Faut-il remplacer toute la baie si elle coince ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Dans la très grande majorité des cas, non. Le problème vient des roulettes, du rail ou du réglage, pas de la structure de la baie elle-même. Un remplacement complet n\'est nécessaire que si le cadre est déformé ou si le vitrage est cassé. Un diagnostic permettra de déterminer la solution la plus économique.',
      },
    },
  ],
};

const jsonLdBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/', name: 'Accueil' } },
    { '@type': 'ListItem', position: 2, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/blog/', name: 'Blog' } },
    { '@type': 'ListItem', position: 3, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/blog/baie-coulissante-qui-coince-que-faire/', name: 'Baie coulissante qui coince' } },
  ],
};

export default function ArticleBaieQuiCoince() {
  return (
    <>
      <SEO
        title="Baie coulissante qui coince : causes et solutions"
        description="Baie coulissante dure à ouvrir ou qui coince ? Causes, vérifications à faire soi-même et solutions. Intervention pro 24-48h dans l'Hérault. DFPRO."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/blog/baie-coulissante-qui-coince-que-faire/"
      />
      <BlogArticleLayout
        heroImage="/images/blog-baie-coince.jpg"
        title="Baie coulissante qui coince ou dure à ouvrir : que faire"
        breadcrumb={breadcrumb}
        jsonLdBlogPosting={jsonLdBlogPosting}
        jsonLdFAQ={jsonLdFAQ}
        jsonLdBreadcrumb={jsonLdBreadcrumb}
        datePublished="20 juin 2026"
        dateModified="26 juillet 2026"
      >
        <p>
          Vous poussez votre baie vitrée et elle résiste. Vous devez forcer, elle émet un bruit de raclement, ou pire, elle refuse tout simplement de bouger. Une baie coulissante qui coince, c&apos;est un signal d&apos;alarme à ne pas ignorer : continuer à forcer peut aggraver le problème, tordre un rail ou arracher une roulette, avec des conséquences bien plus coûteuses. Dans cet article, nous examinons les causes les plus fréquentes et ce que vous pouvez faire — sans risque — avant de contacter un réparateur dans l&apos;Hérault.
        </p>

        <h2>Cause n°1 : les roulettes sont usées ou grippées</h2>
        <p>
          C&apos;est de loin la cause la plus fréquente d&apos;une baie qui coince. Les roulettes, logées dans le profilé inférieur du vantail, supportent tout le poids du vitrage. Avec le temps, le sable, la poussière et — sur le littoral héraultais — les dépôts salins s&apos;infiltrent dans le roulement. Le galet ne tourne plus, il frotte, et la baie devient un véritable bras de fer à chaque ouverture. La solution passe par un <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement des roulettes et galets</Link>, une opération qui nécessite la dépose du vantail et doit être réalisée par un professionnel.
        </p>

        <h2>Cause n°2 : le rail est encrassé ou déformé</h2>
        <p>
          Le rail inférieur sur lequel glissent les roulettes est directement exposé aux agressions extérieures. Poussière, sable fin, feuilles mortes, poils d&apos;animaux : tout s&apos;y accumule. Sur la durée, ces débris se compactent et forment une couche abrasive qui freine le coulissement. Dans certains cas, un choc — un meuble lourd traîné sur le rail, par exemple — peut déformer le profilé aluminium et créer un point dur infranchissable. Un simple nettoyage à l&apos;aspirateur et au chiffon humide peut parfois résoudre le problème, mais un rail déformé nécessite une <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réparation ou un remplacement du rail</Link>.
        </p>

        <h2>Cause n°3 : le vantail est désaligné</h2>
        <p>
          Un vantail qui frotte sur le dormant en partie haute ou latérale est souvent le symptôme d&apos;un désalignement. Plusieurs facteurs peuvent en être à l&apos;origine : un tassement du bâti (fréquent dans les constructions récentes), une vis de réglage qui s&apos;est desserrée avec les vibrations, ou une usure asymétrique des roulettes. Ce type de problème se règle généralement par un <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage complet de la baie coulissante</Link>, qui inclut la hauteur, l&apos;alignement et la compression des joints. C&apos;est une opération technique qui redonne à la menuiserie son fonctionnement d&apos;origine.
        </p>

        {/* CTA milieu */}
        <div className="my-8 p-5 md:p-6 bg-background-100 rounded-lg border border-accent-500/20 text-center">
          <p className="font-heading font-bold text-primary-500 text-base mb-2">
            Votre baie coulissante coince dans l&apos;Hérault ?
          </p>
          <p className="text-sm text-foreground-500 font-body mb-4">
            Ne forcez pas, vous risqueriez d&apos;aggraver la panne. Appelez-nous pour un diagnostic gratuit.
          </p>
          <a href="tel:+33980802242" className="btn-call text-sm px-6 py-3 whitespace-nowrap inline-flex">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-sm"></i>
            </span>
            09 80 80 22 42
          </a>
        </div>

        <h2>Ce que vous pouvez vérifier vous-même, sans risque</h2>
        <p>
          Avant d&apos;appeler un professionnel, quelques vérifications simples peuvent vous éclairer sur l&apos;origine du blocage. Inspectez d&apos;abord le rail à la lumière du jour : voyez-vous des obstacles, des débris accumulés, des traces de frottement anormales ? Passez la main — doucement, sans forcer — pour sentir une éventuelle déformation. Regardez également le haut du vantail : un joint décollé ou une butée déplacée peut parfois bloquer la course. En revanche, une règle d&apos;or : <strong>ne forcez jamais</strong>. Si la baie ne coulisse pas avec une pression raisonnable, il y a un problème mécanique sous-jacent. Forcer ne fera que transformer une réparation simple — comme un changement de roulettes — en une réparation lourde — comme un rail à remplacer.
        </p>

        <h2>Le cas particulier des baies en bord de mer</h2>
        <p>
          Dans l&apos;Hérault, les communes du littoral comme Sète, Frontignan, Agde, La Grande-Motte ou Marseillan sont particulièrement exposées au phénomène de corrosion saline. L&apos;air chargé en sel, combiné à l&apos;humidité ambiante, attaque les parties métalliques des roulettes et du rail. Ce phénomène est progressif mais inéluctable sans entretien. Si vous habitez en bord de mer, un nettoyage régulier du rail à l&apos;eau claire — sans produit chimique — est le meilleur moyen de retarder l&apos;apparition de points durs. Nous le recommandons à tous nos clients du littoral héraultais après chaque intervention.
        </p>

        <h2>Quand faut-il faire appel à un réparateur ?</h2>
        <p>
          Si le nettoyage du rail ne change rien, si la baie émet des bruits métalliques, ou si vous constatez un affaissement visible du vantail, il est temps de contacter un professionnel. Nos techniciens DFPRO Hérault se déplacent à votre domicile dans toute la métropole de Montpellier et le département. Ils réalisent un diagnostic complet, identifient l&apos;origine exacte du blocage, et vous proposent une solution chiffrée — devis gratuit et sans engagement. Dans la plupart des cas, la réparation peut être effectuée le jour même, avec des pièces détachées que nous avons en stock pour les marques les plus courantes.
        </p>

        <h2>Questions fréquentes</h2>

        <h3>Est-ce dangereux d&apos;avoir une baie coulissante qui coince ?</h3>
        <p>
          Une baie qui coince n&apos;est pas dangereuse en elle-même si elle reste stable dans son rail. En revanche, si elle déraille partiellement ou si le vantail penche, le risque de sortie de rail et de chute est réel — surtout avec un vitrage lourd. C&apos;est pourquoi nous déconseillons formellement de forcer sur une baie récalcitrante et recommandons une intervention rapide.
        </p>

        <h3>Le problème peut-il venir du cadre et pas des roulettes ?</h3>
        <p>
          Oui, c&apos;est possible mais plus rare. Un cadre en aluminium peut se déformer suite à un choc important ou à un mouvement de structure du bâtiment. Dans ce cas, le problème est plus complexe et peut nécessiter un remplacement partiel ou total de la menuiserie. Le diagnostic sur place permet de trancher entre une réparation simple et une intervention plus lourde.
        </p>
      </BlogArticleLayout>
    </>
  );
}